import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useState } from 'react'
import {
    INPUT_MODES,
    NAMEID_FORMATS,
    SsoFieldsValues,
    SsoInputMode,
} from './SsoConnectionFormFields.helpers'

/**
 * Read-only redacted preview shown when a sensitive field
 * (signing cert / metadata XML) is loaded with a pre-populated
 * value and the operator hasn't asked to reveal it yet.
 *
 * Why this exists: the 2026-05-01 re-audit flagged that cert PEM
 * and full metadata XML were rendered into the DOM by default on
 * the Edit page — bystander-shoulder-surf material that doesn't
 * meaningfully need to be visible just because someone opened the
 * row. Hiding-by-default keeps the values out of casual view; the
 * Show toggle is a single click for staff who actually need to
 * read or edit them.
 */
function RedactedField({
    label,
    helperText,
    charCount,
    onReveal,
}: {
    label: string
    helperText: string
    charCount: number
    onReveal: () => void
}) {
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
            >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                    >
                        {label}
                    </Typography>
                    <Typography
                        variant="body2"
                        component="div"
                        sx={{ fontFamily: 'monospace', mt: 0.5 }}
                    >
                        {'•'.repeat(20)} ({charCount.toLocaleString()} chars
                        stored)
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.5 }}
                    >
                        {helperText}
                    </Typography>
                </Box>
                <Button
                    onClick={onReveal}
                    startIcon={<VisibilityIcon />}
                    size="small"
                    variant="outlined"
                >
                    Show
                </Button>
            </Stack>
        </Paper>
    )
}

// Shared field surface for Create and Edit SSO-connection pages.
// Renders everything below the org+slug pair (which the parent
// pages render differently — picker+editable on Create, read-only
// on Edit). Wraps the IdP-config bit in a tabbed control so staff
// can choose between EntityID+URL+Cert / Metadata URL / Metadata
// XML modes.
//
// Constants, schema, helpers, and the mode-detection logic live in
// `./SsoConnectionFormFields.helpers.ts` so this file is component-
// only (which keeps Vite's react-refresh happy).
//
// Mode-tab semantics:
//   - Switching tabs only toggles which mode-specific field is
//     visible. The shared fields (entity ID, SSO URL, NameID,
//     email domains, flags) are constant across modes.
//   - The "non-active" mode fields stay in form state but are
//     null-ed out on submit (see `payloadForMode` in helpers).
//     This means moving away from a tab and back doesn't lose
//     what was typed in that tab earlier — small QoL.

type Props = {
    values: SsoFieldsValues
    setValue: <K extends keyof SsoFieldsValues>(
        key: K,
        value: SsoFieldsValues[K]
    ) => void
    errors: Partial<Record<keyof SsoFieldsValues, string>>
}

export default function SsoConnectionFormFields({
    values,
    setValue,
    errors,
}: Props) {
    // Reveal state for the two sensitive fields. Initial state at
    // first mount: hidden iff the field was already populated (the
    // Edit-loaded scenario); shown otherwise (the Create/Onboard
    // empty-form scenario, where hiding-by-default would just be an
    // extra click before the operator can type anything).
    //
    // The initialiser fires once per mount, so subsequent prop
    // changes don't reset reveal state — letting an operator hide
    // again after editing requires clicking Hide explicitly. That's
    // the right shape: hiding is intentional, not automatic.
    const [revealCert, setRevealCert] = useState(
        () => !values.idp_signing_cert || values.idp_signing_cert.length === 0
    )
    const [revealXml, setRevealXml] = useState(
        () => !values.idp_metadata_xml || values.idp_metadata_xml.length === 0
    )

    return (
        <>
            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    IdP configuration source
                </Typography>
                <Tabs
                    value={values.input_mode}
                    onChange={(_e, value: SsoInputMode) =>
                        setValue('input_mode', value)
                    }
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    variant="fullWidth"
                >
                    {INPUT_MODES.map((mode) => (
                        <Tab
                            key={mode.value}
                            label={mode.label}
                            value={mode.value}
                        />
                    ))}
                </Tabs>

                {values.input_mode !== 'triple' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Heads-up: the metadata-ingestion cron that auto-
                        extracts entity ID, SSO URL, and signing cert from IdP
                        metadata is still tracked separately in the Phase 2
                        backlog. For now, please fill in the entity ID and SSO
                        URL fields below by hand even when supplying metadata.
                    </Alert>
                )}
            </Box>

            <TextField
                label="IdP entity ID"
                helperText={
                    errors.idp_entity_id ??
                    'The <md:EntityDescriptor entityID="..."> value from the IdP metadata.'
                }
                error={!!errors.idp_entity_id}
                value={values.idp_entity_id}
                onChange={(e) => setValue('idp_entity_id', e.target.value)}
                required
                fullWidth
            />

            <TextField
                label="IdP SSO URL"
                helperText={
                    errors.idp_sso_url ??
                    'The <SingleSignOnService Location="..."> URL the browser POSTs the AuthnRequest to.'
                }
                error={!!errors.idp_sso_url}
                value={values.idp_sso_url}
                onChange={(e) => setValue('idp_sso_url', e.target.value)}
                required
                fullWidth
            />

            {values.input_mode === 'triple' &&
                (revealCert ? (
                    <Box>
                        <TextField
                            label="IdP signing certificate (PEM body)"
                            helperText={
                                errors.idp_signing_cert ??
                                'Paste just the base64 between BEGIN/END CERTIFICATE lines (no header/footer).'
                            }
                            error={!!errors.idp_signing_cert}
                            value={values.idp_signing_cert ?? ''}
                            onChange={(e) =>
                                setValue('idp_signing_cert', e.target.value)
                            }
                            required
                            fullWidth
                            multiline
                            minRows={6}
                            maxRows={14}
                            inputProps={{
                                style: {
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                },
                            }}
                        />
                        {/* Hide button only matters when there's a value to
                            hide back. Don't render it for empty fields. */}
                        {values.idp_signing_cert &&
                            values.idp_signing_cert.length > 0 && (
                                <Button
                                    onClick={() => setRevealCert(false)}
                                    startIcon={<VisibilityOffIcon />}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                >
                                    Hide
                                </Button>
                            )}
                    </Box>
                ) : (
                    <RedactedField
                        label="IdP signing certificate (PEM body)"
                        helperText="Hidden by default to keep cert content out of casual view. Click Show to reveal or rotate."
                        charCount={(values.idp_signing_cert ?? '').length}
                        onReveal={() => setRevealCert(true)}
                    />
                ))}

            {values.input_mode === 'metadata_url' && (
                <TextField
                    label="IdP metadata URL"
                    helperText={
                        errors.idp_metadata_url ??
                        'A fully-qualified URL hosting the IdP metadata XML, e.g. https://idp.example.edu/idp/shibboleth. The (still-pending) metadata-ingestion cron will fetch and cache the cert from here.'
                    }
                    error={!!errors.idp_metadata_url}
                    value={values.idp_metadata_url ?? ''}
                    onChange={(e) =>
                        setValue('idp_metadata_url', e.target.value)
                    }
                    required
                    fullWidth
                />
            )}

            {values.input_mode === 'metadata_xml' &&
                (revealXml ? (
                    <Box>
                        <TextField
                            label="IdP metadata XML"
                            helperText={
                                errors.idp_metadata_xml ??
                                'Paste the full <md:EntityDescriptor> document. Stored verbatim until the metadata-ingestion cron parses it.'
                            }
                            error={!!errors.idp_metadata_xml}
                            value={values.idp_metadata_xml ?? ''}
                            onChange={(e) =>
                                setValue('idp_metadata_xml', e.target.value)
                            }
                            required
                            fullWidth
                            multiline
                            minRows={8}
                            maxRows={20}
                            inputProps={{
                                style: {
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                },
                            }}
                        />
                        {values.idp_metadata_xml &&
                            values.idp_metadata_xml.length > 0 && (
                                <Button
                                    onClick={() => setRevealXml(false)}
                                    startIcon={<VisibilityOffIcon />}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                >
                                    Hide
                                </Button>
                            )}
                    </Box>
                ) : (
                    <RedactedField
                        label="IdP metadata XML"
                        helperText="Hidden by default to keep XML content out of casual view. Click Show to reveal or replace."
                        charCount={(values.idp_metadata_xml ?? '').length}
                        onReveal={() => setRevealXml(true)}
                    />
                ))}

            <TextField
                select
                label="NameID format"
                value={values.nameid_format}
                onChange={(e) => setValue('nameid_format', e.target.value)}
                fullWidth
                helperText="Persistent is the recommended default for stable per-user matching."
            >
                {NAMEID_FORMATS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                ))}
            </TextField>

            <TextField
                label="Username attribute (optional, advanced)"
                helperText={
                    errors.username_attribute ??
                    "Leave blank for most IdPs. Set this only when the IdP's `email` attribute carries a different domain than the user's login UPN (common with AD / Azure AD). Examples: 'upn' (Azure AD), 'eduPersonPrincipalName' (Shibboleth). When set, the first-link gate keys on this attribute's domain instead of `email`'s. See docs/onboard-tenant.md §1.4."
                }
                error={!!errors.username_attribute}
                value={values.username_attribute ?? ''}
                onChange={(e) => setValue('username_attribute', e.target.value)}
                fullWidth
            />

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Attribute mapping (advanced)
                </Typography>
                <Stack spacing={2}>
                    <TextField
                        label="Email attribute"
                        helperText={
                            errors.attribute_email ??
                            "IdP attribute that carries the user's email. Shibboleth: `mail`. Azure AD: the `http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress` URI, or add a short `email` claim IdP-side. Blank → the SP falls back to its built-in email/mail lookup."
                        }
                        error={!!errors.attribute_email}
                        value={values.attribute_email}
                        onChange={(e) =>
                            setValue('attribute_email', e.target.value)
                        }
                        fullWidth
                    />
                    <TextField
                        label="First-name attribute"
                        helperText={
                            errors.attribute_first_name ??
                            'IdP attribute for the given name. e.g. `givenName` (Shibboleth/LDAP) or the Azure AD givenname claim. Optional — display only.'
                        }
                        error={!!errors.attribute_first_name}
                        value={values.attribute_first_name}
                        onChange={(e) =>
                            setValue('attribute_first_name', e.target.value)
                        }
                        fullWidth
                    />
                    <TextField
                        label="Last-name attribute"
                        helperText={
                            errors.attribute_last_name ??
                            'IdP attribute for the surname. e.g. `sn` (Shibboleth/LDAP) or the Azure AD surname claim. Optional — display only.'
                        }
                        error={!!errors.attribute_last_name}
                        value={values.attribute_last_name}
                        onChange={(e) =>
                            setValue('attribute_last_name', e.target.value)
                        }
                        fullWidth
                    />
                </Stack>
            </Box>

            <TextField
                label="Email domain(s) for login discovery (optional)"
                helperText={
                    errors.email_domains_raw ??
                    'Comma- or space-separated. Lowercased on save. Used to map a typed email back to this connection on the login screen. e.g. umn.edu, alumni.umn.edu'
                }
                error={!!errors.email_domains_raw}
                value={values.email_domains_raw ?? ''}
                onChange={(e) => setValue('email_domains_raw', e.target.value)}
                fullWidth
            />

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Flags
                </Typography>
                <Stack>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.enabled}
                                onChange={(e) =>
                                    setValue('enabled', e.target.checked)
                                }
                            />
                        }
                        label="Enabled — connection is live and accepts logins"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.enforce_sso}
                                onChange={(e) =>
                                    setValue('enforce_sso', e.target.checked)
                                }
                            />
                        }
                        label="Enforce SSO — password & magic-link login disabled for users in this org"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.allow_idp_initiated}
                                onChange={(e) =>
                                    setValue(
                                        'allow_idp_initiated',
                                        e.target.checked
                                    )
                                }
                            />
                        }
                        label="Allow IdP-initiated logins (un-RelayStated POSTs to /sso/saml/acs)"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.require_response_signature}
                                onChange={(e) =>
                                    setValue(
                                        'require_response_signature',
                                        e.target.checked
                                    )
                                }
                            />
                        }
                        label="Require signed SAML response — IdP must sign the <Response> envelope, not just the assertion. Most IdPs (Entra/Azure AD, Okta) sign the assertion only; leave off unless this IdP signs both."
                    />
                </Stack>
            </Box>
        </>
    )
}
